/**
 * Authentication Redux Slice
 *
 * This module manages the authentication state for the application using Redux Toolkit.
 * It handles user authentication, login/logout flows, and various user-related state updates.
 *
 * State includes:
 * - Current user information
 * - Authentication loading status
 * - Redirect information for post-login navigation
 * - Facebook integration status
 * - Lead service script configurations
 *
 * @module store/slices/auth
 */

import { createSlice } from "@reduxjs/toolkit";
import authConfig from "../../config/auth";

/**
 * Initial authentication state
 *
 * @property {Object|null} user - User data when authenticated, null when not authenticated
 * @property {boolean} loading - Indicates if authentication state is being determined
 * @property {string|null} redirect - URL to redirect to after authentication
 */
const initialState = {
  user: null, // User data object, null when not authenticated
  loading: true, // Initially true while checking for existing auth
  redirect: null // Path to redirect after login if coming from protected route
};
/**
 * Authentication slice with reducers for managing auth state
 */
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Initializes the application auth state
     * Sets loading to false and captures redirect URL if needed
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with payload containing redirect info
     */
    initApp(state, action) {
      state.loading = false;
      // Store current URL for post-login redirect (if not a login page)
      if (
        !action.payload?.value?.includes("login") &&
        action.payload?.type === "redirect"
      ) {
        state.redirect = action.payload.value;
      }
    },

    /**
     * Logs out the current user
     * Removes auth data from localStorage and resets state
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with payload containing redirect info
     */
    logout(state, action) {
      // Store current URL for post-login redirect (if not a login page)
      if (
        !action.payload?.value?.includes("login") &&
        action.payload?.type === "redirect"
      ) {
        state.redirect = action.payload.value;
      }
      // Clear authentication data from localStorage
      window.localStorage.removeItem(authConfig.userData);
      window.localStorage.removeItem(authConfig.storageTokenKeyName);

      // Reset state
      state.user = null;
      state.loading = false;
    },

    /**
     * Handles successful login
     * Stores user data and token in localStorage and updates state
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with payload containing auth data
     */
    login(state, action) {
      // Clear redirect as we're now authenticated
      state.redirect = null;
      // Extract auth data from payload
      const { userData, token } = action.payload.data;
      // Store in localStorage for persistence
      localStorage.setItem(authConfig.userData, JSON.stringify(userData));
      localStorage.setItem(authConfig.storageTokenKeyName, token);
      // Update state with user data
      state.user = userData;
    },

    /**
     * Sets user data after fetching current user info
     * Called when checking existing authentication on app load
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with payload containing user data
     */
    setMe(state, action) {
      // Store user data in localStorage
      localStorage.setItem(
        authConfig.userData,
        JSON.stringify(action.payload.userData)
      );
      // Update state
      state.user = action.payload.userData;
      state.loading = false;
    },
    /**
     * Sets the primary Facebook token for the workspace
     * Updates token status and marks specified token as primary
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with payload containing userId
     */
    setPrimaryFbToken(state, action) {
      const { userId } = action.payload;
      const workspace = state.user.workspace;
      const { fbTokens } = workspace;

      // Update workspace with new primary token settings
      state.user.workspace = {
        ...workspace,
        noValidPrimaryTokenExists: false,
        fbTokens: fbTokens.map((fbToken) => ({
          ...fbToken,
          // Set all tokens as non-primary
          isPrimary: false,
          // Mark the selected token as primary and valid
          ...(userId === fbToken.userId._id
            ? { isPrimary: true, isTokenInvalid: false }
            : undefined)
        }))
      };

      // Update localStorage with the new user data
      localStorage.setItem(authConfig.userData, JSON.stringify(state.user));
    },

    /**
     * Updates state after connecting to Facebook
     * Sets user as connected and updates token validity
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with payload containing userId
     */
    connectToFacebook(state, action) {
      const { userId } = action.payload;
      const workspace = state.user.workspace;
      const { fbTokens } = workspace;

      // Update user with Facebook connection status
      state.user = {
        ...state.user,
        isConnectedToFb: true,
        workspace: {
          ...workspace,
          noValidPrimaryTokenExists: false,
          fbTokens: fbTokens.map((fbToken) => ({
            ...fbToken,
            isTokenInvalid:
              userId === fbToken.userId._id || fbToken.isTokenInvalid
          }))
        }
      };

      // Update localStorage with the new user data
      localStorage.setItem(authConfig.userData, JSON.stringify(state.user));
    },

    /**
     * Updates the lead service script in workspace
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with script payload
     */
    setLeadServiceScript(state, action) {
      state.user.workspace.leadServiceScript = action.payload;
    },

    /**
     * Updates the lead service plus script in workspace
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with script payload
     */
    setLeadServicePlusScript(state, action) {
      state.user.workspace.leadServicePlusScript = action.payload;
    },

    /**
     * Updates the filtered lead (reclaimed leads) script in workspace
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with script payload
     */
    setFilteredLeadScript(state, action) {
      state.user.workspace.filteredLeadScript = action.payload;
    },

    /**
     * Updates the appointment confirmation script in workspace
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with script payload
     */
    setAppointmentConfirmationScript(state, action) {
      state.user.workspace.appointmentConfirmationScript = action.payload;
    },

    /**
     * Updates the offer followup script in workspace
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with script payload
     */
    setOfferFollowupScript(state, action) {
      state.user.workspace.offerFollowupScript = action.payload;
    },

    /**
     * Updates the global privacy policy in workspace
     *
     * @param {Object} state - Current auth state
     * @param {Object} action - Action with script payload
     */
    setAMPrivacyPolicy(state, action) {
      state.user.workspace.amPolicyVersion = action.payload;
    },
    setGlobalReclaimConfig(state, action) {
      state.user.workspace.globalReclaimConfig = {
        ...state.user.workspace.globalReclaimConfig,
        ...action.payload
      };
    },
    setDigitalThankyouPage(state, action) {
      state.user.workspace.digitalThankyouPage = {
        ...state.user.workspace.digitalThankyouPage,
        ...action.payload
      };
    }
  }
});

/**
 * Export individual action creators for use in components
 */
export const {
  initApp, // Initialize app auth state
  logout, // Log out the current user
  login, // Handle successful login
  setMe, // Set current user data
  setPrimaryFbToken, // Set primary Facebook token
  connectToFacebook, // Connect user to Facebook
  setLeadServiceScript, // Update lead service script
  setFilteredLeadScript, // Update filtered lead (reclaimed) script
  setAMPrivacyPolicy, // Update global privacy policy
  setLeadServicePlusScript, // Update lead service plus script
  setAppointmentConfirmationScript, // Update appointment confirmation script
  setOfferFollowupScript, // Update offer followup script
  setGlobalReclaimConfig, // Update global reclaim config
  setDigitalThankyouPage // Update digital thank-you page
} = authSlice.actions;

/**
 * Export the reducer for store configuration
 */
export default authSlice.reducer;
